import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { LongPressProgressRing } from "./LongPressProgressRing";
import type { PartyShellPalette } from "../theme/theme";

/** Hold this long while pressing to show the word; releasing hides it again. */
const LONG_PRESS_MS = 750;

/**
 * Shows a “long press to reveal” prompt until the user long-presses; the real word is visible only while they keep holding.
 */
export function LongPressWordPeek({ word }: { word: string }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const party = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const [progress, setProgress] = useState(0);
  const [pressed, setPressed] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const reset = () => {
    stopRaf();
    startRef.current = null;
    setProgress(0);
    setPressed(false);
  };

  const tick = () => {
    const start = startRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    const p = Math.min(1, elapsed / LONG_PRESS_MS);
    setProgress(p);
    if (p >= 1) {
      stopRaf();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    e.preventDefault();
    const el = e.currentTarget;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    startRef.current = performance.now();
    setPressed(true);
    stopRaf();
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    try {
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }
    reset();
  };

  const visible = pressed && progress >= 1;

  return (
    <Box sx={{ width: "100%" }}>
      <Button
        type="button"
        variant="text"
        disableRipple
        onPointerDown={onPointerDown}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={(e) => {
          try {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) return;
          } catch {
            /* ignore */
          }
          reset();
        }}
        sx={{
          p: 2,
          width: "100%",
          minWidth: 0,
          minHeight: 200,
          borderRadius: 3,
          position: "relative",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          display: "block",
          bgcolor: "transparent",
          "&:hover": { bgcolor: "rgba(255,255,255,0.04)" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {pressed && progress < 1 ? (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: 200,
                height: 200,
                marginLeft: "-100px",
                marginTop: "-100px",
                pointerEvents: "none",
              }}
            >
              <LongPressProgressRing
                progress={progress}
                trackStroke={`color-mix(in oklab, ${party.hot} 45%, transparent)`}
                progressStroke={party.hot}
              />
            </Box>
          ) : null}

          <Typography
            className={visible ? "party-display" : undefined}
            sx={{
              fontSize: visible ? "clamp(28px, 8vw, 44px)" : "clamp(16px, 4vw, 22px)",
              lineHeight: 1.2,
              textAlign: "center",
              px: 1,
              color: party.paperInk,
              position: "relative",
              zIndex: 1,
              wordBreak: "break-word",
              fontWeight: visible ? 900 : 800,
              fontFamily: visible ? "var(--font-display)" : "var(--font-body)",
            }}
          >
            {visible ? word : !word.trim() ? "—" : t("party.playWhoAmI.longPressToReveal")}
          </Typography>
        </Box>
      </Button>
      <Typography sx={{ mt: 1.5, textAlign: "center", fontSize: 13, color: party.inkDim, lineHeight: 1.45, px: 1 }}>
        {t("party.playWhoAmI.pressHoldPeek")}
      </Typography>
    </Box>
  );
}
